import { ProtectedRoute } from '@/auth/ProtectedRoute';
import { RouteErrorPage } from '@/components/RouteErrorPage';
import { AdminLayout } from '@/layouts/AdminLayout';
import { lazyWithRecovery } from '@/lib/lazyWithRecovery';
import { AdminGuard as RoleGuard } from '@/pages/admin/_shared/guard';
import LoginPage from '@/pages/login';
import SsoPage from '@/pages/sso';
import { Spin } from 'antd';
import { type ReactNode, Suspense } from 'react';
import { Navigate, createBrowserRouter } from 'react-router-dom';

const UserUsagePage = lazyWithRecovery(() => import('@/pages/admin/user-usage'));
const OpsScreenPage = lazyWithRecovery(() => import('@/pages/admin/ops-screen'));
const AccountPage = lazyWithRecovery(() => import('@/pages/admin/account'));
const AdminStaffPage = lazyWithRecovery(() => import('@/pages/admin/admin-users'));
const AiGatewayPage = lazyWithRecovery(() => import('@/pages/admin/ai-gateway'));
const AdminAuditPage = lazyWithRecovery(() => import('@/pages/admin/audit'));
const BackupPage = lazyWithRecovery(() => import('@/pages/admin/backup'));
const ContentSafetyPage = lazyWithRecovery(() => import('@/pages/admin/content-safety'));
const AdminDashboardPage = lazyWithRecovery(() => import('@/pages/admin/dashboard'));
const DramaGovernancePage = lazyWithRecovery(() => import('@/pages/admin/drama'));
const DramaTeamsPage = lazyWithRecovery(() => import('@/pages/admin/drama/teams'));
const CatalogHistoryPage = lazyWithRecovery(() => import('@/pages/admin/drama/catalog-history'));
const AssetModerationPage = lazyWithRecovery(() => import('@/pages/admin/drama/asset-moderation'));
const AdminFeatureFlagsPage = lazyWithRecovery(() => import('@/pages/admin/feature-flags'));
const FinancePage = lazyWithRecovery(() => import('@/pages/admin/finance'));
const ImagesPage = lazyWithRecovery(() => import('@/pages/admin/images'));
const InstancesPage = lazyWithRecovery(() => import('@/pages/admin/instances'));
const MigrationConsolePage = lazyWithRecovery(() => import('@/pages/admin/migration'));
const MonitoringPage = lazyWithRecovery(() => import('@/pages/admin/monitoring'));
const OperationsPage = lazyWithRecovery(() => import('@/pages/admin/operations'));
const PaymentPage = lazyWithRecovery(() => import('@/pages/admin/payment'));
const NotificationsPage = lazyWithRecovery(() => import('@/pages/admin/notifications'));
const TrialApplicationsPage = lazyWithRecovery(() => import('@/pages/admin/trial-applications'));
const ReleaseCenterPage = lazyWithRecovery(() => import('@/pages/admin/releases'));
const ResourcePoolPage = lazyWithRecovery(() => import('@/pages/admin/resource-pool'));
const AdminSkillsPage = lazyWithRecovery(() => import('@/pages/admin/skills'));
const AdminTaskCenterPage = lazyWithRecovery(() => import('@/pages/admin/task-center'));
const VideoSupplyPage = lazyWithRecovery(() => import('@/pages/admin/video-supply'));
const VideoSupplyPoolDetailPage = lazyWithRecovery(
  () => import('@/pages/admin/video-supply/PoolDetailPage'),
);
const VideoSupplyGatewayPoolDetailPage = lazyWithRecovery(
  () => import('@/pages/admin/video-supply/GatewayPoolDetailPage'),
);
const VideoModelsPage = lazyWithRecovery(() => import('@/pages/admin/video-models'));
const VideoModelDetailPage = lazyWithRecovery(
  () => import('@/pages/admin/video-models/ModelDetailPage'),
);
const ChannelsPage = lazyWithRecovery(() => import('@/pages/admin/channels'));
const ChannelUsagePage = lazyWithRecovery(() => import('@/pages/admin/channel-usage'));
const VendorsPage = lazyWithRecovery(() => import('@/pages/admin/vendors'));

function deferred(page: ReactNode) {
  return (
    <Suspense
      fallback={
        <div style={{ display: 'grid', minHeight: 240, placeItems: 'center' }}>
          <Spin tip="正在加载功能" />
        </div>
      }
    >
      {page}
    </Suspense>
  );
}

/**
 * 独立运维控制台路由表。整个 app 都是运维后台：
 *   /login  账户中心登录（管理员直接在 admin.<host> 登录）
 *   /sso    从产品站带 token 免二次登录跳转的落地页（?token=<suite.jwt>）
 *   /*      受保护 + 运维角色校验后的治理页（无 /admin 前缀，直接挂根）
 *
 * 鉴权分两层：ProtectedRoute（有无 token）→ RoleGuard（后端 /auth/me 校验
 * super/ops/cs/finance 角色，无授权 403）。
 */
const basename = (import.meta.env.BASE_URL || '/').replace(/\/$/, '') || undefined;

export const router = createBrowserRouter(
  [
    { path: '/login', element: <LoginPage /> },
    { path: '/sso', element: <SsoPage /> },
    {
      path: '/',
      errorElement: <RouteErrorPage />,
      element: (
        <ProtectedRoute>
          <RoleGuard>
            <AdminLayout />
          </RoleGuard>
        </ProtectedRoute>
      ),
      children: [
        { index: true, element: deferred(<AdminDashboardPage />) },
        { path: 'user-usage', element: deferred(<UserUsagePage />) },
        { path: 'ops-screen', element: deferred(<OpsScreenPage />) },
        // 设计文档沿用了 /admin/ops-screen 的写法；控制台路由无 /admin 前缀，旧链接重定向。
        { path: 'admin/ops-screen', element: <Navigate to="/ops-screen" replace /> },
        { path: 'operations', element: deferred(<OperationsPage />) },
        { path: 'instances', element: deferred(<InstancesPage />) },
        { path: 'drama', element: deferred(<DramaGovernancePage />) },
        { path: 'drama/teams', element: deferred(<DramaTeamsPage />) },
        { path: 'drama/catalog-history', element: deferred(<CatalogHistoryPage />) },
        { path: 'drama/asset-moderation', element: deferred(<AssetModerationPage />) },
        { path: 'users', element: <Navigate to="/account" replace /> },
        { path: 'resource-pool', element: deferred(<ResourcePoolPage />) },
        { path: 'images', element: deferred(<ImagesPage />) },
        { path: 'plans', element: <Navigate to="/account" replace /> },
        { path: 'backup', element: deferred(<BackupPage />) },
        { path: 'monitoring', element: deferred(<MonitoringPage />) },
        { path: 'releases', element: deferred(<ReleaseCenterPage />) },
        { path: 'skills', element: deferred(<AdminSkillsPage />) },
        { path: 'audit', element: deferred(<AdminAuditPage />) },
        { path: 'account', element: deferred(<AccountPage />) },
        { path: 'account/users/:userId', element: deferred(<AccountPage />) },
        { path: 'account/:section', element: deferred(<AccountPage />) },
        { path: 'payment', element: deferred(<PaymentPage />) },
        { path: 'mail', element: deferred(<NotificationsPage />) },
        { path: 'notifications', element: deferred(<NotificationsPage />) },
        { path: 'trial-applications', element: deferred(<TrialApplicationsPage />) },
        { path: 'content-safety', element: deferred(<ContentSafetyPage />) },
        { path: 'ai-gateway', element: deferred(<AiGatewayPage />) },
        { path: 'vendors', element: deferred(<VendorsPage />) },
        { path: 'channels', element: deferred(<ChannelsPage />) },
        { path: 'channel-usage', element: deferred(<ChannelUsagePage />) },
        { path: 'video-supply', element: deferred(<VideoSupplyPage />) },
        {
          path: 'video-supply/pools/:poolId',
          element: deferred(<VideoSupplyPoolDetailPage />),
        },
        {
          path: 'video-supply/gateway-pools/:poolId',
          element: deferred(<VideoSupplyGatewayPoolDetailPage />),
        },
        { path: 'video-models', element: deferred(<VideoModelsPage />) },
        { path: 'video-models/:modelKey', element: deferred(<VideoModelDetailPage />) },
        { path: 'migration', element: deferred(<MigrationConsolePage />) },
        { path: 'task-center', element: deferred(<AdminTaskCenterPage />) },
        { path: 'feature-flags', element: deferred(<AdminFeatureFlagsPage />) },
        { path: 'finance', element: deferred(<FinancePage />) },
        { path: 'admin-users', element: deferred(<AdminStaffPage />) },
      ],
    },
    { path: '*', element: <Navigate to="/" replace /> },
  ],
  { basename },
);
